import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useQueryState } from 'nuqs';
import { useSession } from '@/providers/session-provider';
import {
  useGetOrganization,
  useGetOrganizationList,
} from '@/config/sales/organization/client';
import { setSessionValues } from '@/providers/session-provider/set-session-values';
import { USER_QUERY_KEY } from '@/providers/session-provider/use-current-user';
import { ChevronsUpDownIcon } from 'lucide-react';
import { Icons } from '@/components/icons';

export function OrganizationSelector() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useQueryState('filter');
  const {
    data: { session, user },
  } = useSession();
  const currOrgId = session.organizationId;
  const { data, isPending: isLoadingOrganizations } = useGetOrganizationList({
    queryParams: {
      page: 1,
      pageSize: 25,
      filters: {
        id: {
          '__in!': user.organization.id,
        },
      },
    },
  });

  const hasMoreThanOneOrganization =
    data && data.list.length > 0 && !isLoadingOrganizations;

  const { data: mainOrg, isLoading: isLoadingMainOrg } = useGetOrganization({
    id: user.organization.id,
    enabled: hasMoreThanOneOrganization,
  });

  const currentOrg = data?.list.find((org) => org?.id === currOrgId) || mainOrg;

  const chooseOrg = (orgId: string) => {
    if (orgId === currOrgId) {
      return;
    }
    if (filters) {
      setFilters(null);
    }
    setSessionValues({ organizationId: orgId });
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.at(0) !== USER_QUERY_KEY;
      },
    });
    queryClient.invalidateQueries();
  };

  const isLoading = isLoadingOrganizations || isLoadingMainOrg;

  if (!hasMoreThanOneOrganization && !isLoading) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className='flex w-full items-center justify-between gap-2 overflow-hidden rounded-md border px-2 py-1 text-sm'>
        <div className='min-w-0 flex-1 truncate text-left'>
          {isLoading ? 'Laddar...' : currentOrg?.name}{' '}
        </div>
        <ChevronsUpDownIcon className='size-4 shrink-0' />
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' className='w-72'>
        <DropdownMenuLabel>Välj organisation</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isLoading && (
          <DropdownMenuItem className='flex w-full items-center justify-between'>
            <span className='text-xs text-muted-foreground'>
              Laddar organisationer...
            </span>
            <Icons.loader className='size-4 animate-spin' />
          </DropdownMenuItem>
        )}
        {!isLoading && (
          <>
            <DropdownMenuCheckboxItem
              checked={currOrgId === user.organization.id}
              onCheckedChange={() => chooseOrg(user.organization.id)}
              className='flex items-center gap-1'
            >
              <span className='font-medium'>{mainOrg?.name}</span>
              <span className='text-xs text-muted-foreground'>
                (Huvudorganisation)
              </span>
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            {data?.list.map((org) => (
              <DropdownMenuCheckboxItem
                key={org.id}
                checked={org.id === currOrgId}
                onCheckedChange={() => chooseOrg(org.id)}
              >
                {org.name}
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
